import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Location } from '@court-sync/shared';
import { locationService, getPublicLocations } from '../services/location';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';
import { unwrapApiDataOrEmpty } from './unwrap';

export function useLocations(
  academyId?: string,
  options?: { includeInactive?: boolean; enabled?: boolean }
) {
  const includeInactive = options?.includeInactive ?? false;
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();
  const queryKey = queryKeys.locations.list(academyId ?? '', includeInactive);

  const { data: locations = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await locationService.getLocations(academyId!, includeInactive);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
  }, [queryClient]);

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    locations,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    invalidate,
  };
}

/** Active locations for coach schedule (avoids owner-only `/locations` API). */
export function usePublicLocations(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const queryKey = [...queryKeys.locations.all, 'public', academyId ?? ''] as const;

  const { data: locations = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getPublicLocations(academyId!);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    locations,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
  };
}

/** Read locations from any cached list for an academy. */
export function getLocationsFromCache(
  academyId: string,
  includeInactive = false
): Location[] | undefined {
  return queryClient.getQueryData<Location[]>(
    queryKeys.locations.list(academyId, includeInactive)
  );
}
